import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Tooltip,
  Select,
  Autocomplete,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions } from
'@mui/material';

import TreeSelect from '../TreeSelect';
import TicketAttachmentUpload from './AttachmentUpload';
import { ticketsApi, locationsApi, companiesApi } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';

// Icons
import {
  TipsAndUpdates as TipsAndUpdatesIcon,
  VpnKey as KeyIcon,
  Tablet as TabletIcon,
  Computer as ImportantDevicesIcon,
  Support as SupportIcon,
  Help as HelpIcon,
  PriorityHigh as PriorityHighIcon,
  LowPriority as LowPriorityIcon,
  WifiTetheringError as WifiTetheringErrorIcon,
  DensityMedium as DensityMediumIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CancelOutlined as CancelOutlinedIcon,
  PhotoCamera as PhotoCameraIcon,
  Videocam as VideocamIcon,
  Stop as StopIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Devices as DeviceIcon,
  Close as CloseIcon } from
'@mui/icons-material';

import EmergencyShareIcon from '@mui/icons-material/EmergencyShare';

// API functions
import { useThemeSettings } from "../../contexts/ThemeContext";const createTicket = async (formData) => {
  try {
    const response = await ticketsApi.create(formData);
    return response;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

const fetchLocations = async () => {
  try {
    const response = await locationsApi.getAll();
    //console.log('Fetched locations:', response);

    let locationsData = [];
    if (response?.data) {
      if (Array.isArray(response.data)) {
        locationsData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        locationsData = response.data.data;
      } else if (response.data.locations && Array.isArray(response.data.locations)) {
        locationsData = response.data.locations;
      }
    }

    // Transform flat list to tree structure if needed
    const buildLocationTree = (locations, parentId = null) => {
      return locations.
      filter((loc) => (loc.parentId || loc.parent_id || loc.parentLocation) === parentId).
      map((loc) => ({
        id: loc.id || loc._id || loc.location_UUID,
        name: loc.name || loc.friendlyName,
        friendlyName: loc.friendlyName || loc.name,
        locationType: loc.locationType || loc.location_type || 1,
        children: buildLocationTree(locations, loc.id || loc._id || loc.location_UUID)
      }));
    };

    // Check if data is already in tree format
    const hasChildren = locationsData.some((loc) => loc.children && Array.isArray(loc.children));

    if (hasChildren) {
      // Already in tree format
      return { data: locationsData };
    } else {
      // Convert flat list to tree
      const tree = buildLocationTree(locationsData);
      return { data: tree.length > 0 ? tree : locationsData };
    }
  } catch (error) {
    console.error('Error fetching locations:', error);
    // Return empty data on error
    return { data: [] };
  }
};

// Solution Recommendation component
const SolutionRecommendation = ({ ticketDescription, isCreatingTicket, onSolutionsFound }) => {const { t } = useThemeSettings();
  useEffect(() => {
    const fetchSolutions = async () => {
      if (ticketDescription && ticketDescription.length > 20) {
        try {
          // Fetch solutions from API
          const response = await ticketsApi.searchSolutions({
            query: ticketDescription,
            limit: 5
          });

          let solutions = [];
          if (response?.data) {
            if (Array.isArray(response.data)) {
              solutions = response.data;
            } else if (response.data.solutions) {
              solutions = response.data.solutions;
            }
          }

          // Transform the solutions if needed
          const transformedSolutions = solutions.map((solution) => ({
            id: solution.id || solution._id,
            title: solution.title || solution.name,
            description: solution.description,
            stepByStepInstructions: solution.stepByStepInstructions || solution.step_by_step_instructions || '',
            complexity: solution.complexity || 'moderate'
          }));

          if (onSolutionsFound && transformedSolutions.length > 0) {
            onSolutionsFound(transformedSolutions);
          }
        } catch (error) {
          console.error('Error fetching solutions:', error);
          // Silently fail - solutions are optional
        }
      }
    };

    fetchSolutions();
  }, [ticketDescription, onSolutionsFound]);

  if (!ticketDescription || ticketDescription.length <= 20) {
    return null;
  }

  return (
    <Card sx={{ p: 2, mt: 2 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <TipsAndUpdatesIcon />
        <Typography variant="h6" color="primary" sx={{ ml: 1 }}>
          {isCreatingTicket ? "Similar Issues Found - Solutions Available" : "Recommended Solutions"}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {isCreatingTicket ?
        "We found similar issues with possible solutions. Consider trying these before submitting." :
        "Based on the ticket description, these solutions might help resolve the issue:"}
      </Typography>

      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
        <Alert severity="info" sx={{ mb: 2 }}>{t ? t("CreateForm.solutions_will_appear_here_based_your") : "Solutions will appear here based on your ticket description. Please provide a detailed description for better recommendations."}

        </Alert>
      </Paper>
    </Card>);

};

// Form field components
const Field = {
  Text: ({ name, label, required, fullWidth, value, onChange }) =>
  <TextField
    name={name}
    label={label}
    required={required}
    fullWidth={fullWidth}
    value={value || ''}
    onChange={(e) => onChange && onChange(e.target.value)} />,


  Editor: ({ name, sx, value, onChange, placeholder }) =>
  <TextField
    name={name}
    multiline
    rows={6}
    fullWidth
    value={value || ''}
    onChange={(e) => onChange && onChange(e.target.value)}
    sx={sx}
    placeholder={placeholder} />,


  Upload: ({ name, multiple, maxSize, onDelete, accept, t }) =>
  <Box>
      <input
      accept={Object.keys(accept || {}).join(',')}
      style={{ display: 'none' }}
      id="raised-button-file"
      multiple={multiple}
      type="file"
      onChange={(e) => {
      }} />

      <label htmlFor="raised-button-file">
        <Button variant="contained" component="span">{t ? t("CreateForm.upload_files") : "Upload Files"}

      </Button>
      </label>
      <Typography variant="caption" display="block" sx={{ mt: 1 }}>{t ? t("CreateForm.max_file_size") : "Max file size:"}
      {Math.floor(maxSize / (1024 * 1024))}MB
      </Typography>
    </Box>

};

// Main CreateForm component - can be used as standalone or with pre-filled data
export default function CreateForm({
  preSelectedLocation = null, // Pre-selected location when opened from location page
  preSelectedDevice = null, // Pre-selected device when opened from device page
  onClose = null, // Callback when form is closed
  onSuccess = null, // Callback when ticket is created successfully
  embedded = false, // Whether the form is embedded in another page
  fetchingLocation = false // Whether location is being fetched (for device tickets)
}) {const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationTreeOpen, setLocationTreeOpen] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [supportTypes, setSupportTypes] = useState([]);
  const [loadingSupportTypes, setLoadingSupportTypes] = useState(false);

  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState('photo');
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const intervalRef = React.useRef(null);

  // Get user data from auth context
  const { user } = useAuth();
  const userID = user?.id || user?._id || 1;
  const companyID = user?.companyId || user?.company_id || 1;

  // Step states
  const [supportType, setSupportType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(preSelectedLocation);
  const [urgency, setUrgency] = useState('');

  // Update selectedLocation when preSelectedLocation changes
  useEffect(() => {
    if (preSelectedLocation) {
      setSelectedLocation(preSelectedLocation);
    }
  }, [preSelectedLocation]);

  // Form state
  const [formData, setFormData] = useState({
    content: '',
    stepsTried: '',
    attachmentURL: [],
    relatedDevice: preSelectedDevice, // Store device info if provided
    deviceId: preSelectedDevice?.id || null // Device ID for the API
  });

  // Solution recommendation
  const [suggestedSolutions, setSuggestedSolutions] = useState([]);
  const [solutionsChecked, setSolutionsChecked] = useState(false);
  const [showSolutionInfo, setShowSolutionInfo] = useState(false);
  const [solutionsTried, setSolutionsTried] = useState([]);

  // Form methods
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getValues = (field) => formData[field];

  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Mobile detection
  const isMobileDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;

    try {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;
    } catch {
      return false;
    }
  }, []);

  const hasCameraSupport = useMemo(() => {
    if (typeof navigator !== 'undefined') {
      return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }
    return false;
  }, []);

  // Icon mapping for support types
  const getSupportTypeIcon = useCallback((value) => {
    const iconMap = {
      'domotics': <TipsAndUpdatesIcon />,
      'access_control': <KeyIcon />,
      'tablet': <TabletIcon />,
      'emergency_system': <EmergencyShareIcon />,
      'it_pc': <ImportantDevicesIcon />,
      'itpc': <ImportantDevicesIcon />,
      'other': <SupportIcon />
    };
    return iconMap[value] || <SupportIcon />;
  }, []);

  // Load locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      setLoadingLocations(true);
      try {
        const response = await fetchLocations();
        setLocations(response.data);
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };
    loadLocations();
  }, []);

  // Load support types on mount
  useEffect(() => {
    const loadSupportTypes = async () => {
      setLoadingSupportTypes(true);
      try {
        const response = await companiesApi.getSupportTypes();
        if (response.data.success) {
          setSupportTypes(response.data.data);
        }
      } catch (error) {
        console.error('Failed to load support types:', error);
        // Fall back to default support types if API fails
        setSupportTypes([
          { value: 'domotics', label: 'Domotics', enabled: true },
          { value: 'access_control', label: 'Access Control', enabled: true },
          { value: 'tablet', label: 'Tablet', enabled: true },
          { value: 'emergency_system', label: 'Emergency System', enabled: true },
          { value: 'it_pc', label: 'IT for PC', enabled: true },
          { value: 'other', label: 'Other', enabled: true }
        ]);
      } finally {
        setLoadingSupportTypes(false);
      }
    };
    loadSupportTypes();
  }, []);

  // Handle location selection
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setLocationTreeOpen(false);
  };

  // Helper functions
  const formatFileSize = useCallback((bytes) => {
    if (!bytes || typeof bytes !== 'number' || bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const sizeIndex = Math.min(i, sizes.length - 1);

    return parseFloat((bytes / Math.pow(k, sizeIndex)).toFixed(2)) + ' ' + sizes[sizeIndex];
  }, []);

  const getFileIcon = useCallback((file) => {
    if (!file || !file.type) return '📎';

    const type = file.type;
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('sheet')) return '📊';
    return '📎';
  }, []);

  const validateFileClient = useCallback((file) => {
    if (!file) return 'No file provided';
    if (!file.name) return 'File has no name';
    if (typeof file.size !== 'number') return 'File size is invalid';

    const maxSize = 20 * 1024 * 1024 * 1024; // 20GB

    if (file.size > maxSize) {
      return `File "${file.name}" is too large. Maximum size is ${formatFileSize(maxSize)}.`;
    }

    const allowedTypes = [
    'image/', 'video/', 'audio/',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain', 'text/csv'];


    if (!file.type) return 'File type is unknown';

    if (!allowedTypes.some((type) => file.type.startsWith(type) || file.type === type)) {
      return `File type "${file.type}" is not allowed. Supported: images, videos, audio, PDF, Word, Excel, text files.`;
    }

    return null;
  }, [formatFileSize]);

  // Update progress function
  const updateProgressSafely = useCallback((index, updates) => {
    setAttachmentUploadProgress((prev) => {
      if (!Array.isArray(prev)) {
        console.warn('attachmentUploadProgress is not an array, resetting...');
        return [{ index, ...updates }];
      }

      const newProgress = [...prev];
      const existingIndex = newProgress.findIndex((item) => item?.index === index);

      if (existingIndex >= 0) {
        newProgress[existingIndex] = { ...newProgress[existingIndex], ...updates };
      } else {
        newProgress.push({ index, ...updates });
      }

      return newProgress;
    });
  }, []);

  useEffect(() => {
    // Step 6 is always the submission step now (no device issue step anymore)
    const finalSubmitStep = 6;
    if (activeStep === finalSubmitStep) {
      onSubmit();
    }
  }, [activeStep]);

  // Final step trigger
  const finalStep = () => {
    // Step 5 is always the urgency step (last user input step)
    const urgencyStep = 5;
    if (activeStep === urgencyStep) {
      setActiveStep(activeStep + 1); // Move to submission step (6)
    }
  };

  // Handle solutions
  const handleSolutionCheck = (solutionId, checked) => {
    if (checked) {
      setSolutionsTried((prev) => [...prev, solutionId]);
    } else {
      setSolutionsTried((prev) => prev.filter((id) => id !== solutionId));
    }
  };

  const handleSolutionsConfirmed = () => {
    setSolutionsChecked(true);
    setShowSolutionInfo(false);
  };

  const handleSkipSolutions = () => {
    setSolutionsChecked(true);
    setShowSolutionInfo(false);
  };

  // Navigation
  const handleNext = () => {
    // Get the final step (which depends on whether we have a device)
    const finalUrgencyStep = formData.relatedDevice ? 6 : 5;

    if (activeStep === finalUrgencyStep) return; // Do nothing on final step

    switch (activeStep) {
      case 0:
        if (!supportType) {
          alert('Please select a support type.');
          return;
        }
        // Skip location step if we already have a pre-selected location (from device or location page)
        if (selectedLocation) {
          setActiveStep(2); // Skip directly to step 2 (Describe Issue)
          return;
        }
        break;
      case 1:
        if (!selectedLocation) {
          alert('Please select a location.');
          return;
        }
        break;
      case 2:
        if (!getValues('content')) {
          alert('Please provide a description.');
          return;
        }

        // Show solution info if solutions are found and not yet checked
        if (suggestedSolutions.length > 0 && !solutionsChecked) {
          setShowSolutionInfo(true);
          return;
        }
        break;
      case 3:
        if (!getValues('stepsTried')) {
          alert("Please let us know what steps you've already tried.");
          return;
        }
        break;
      case 4:
        // Attachments step - no validation needed (optional)
        break;
      case 5:
        // Urgency step - validate urgency is selected
        if (!urgency) {
          alert("Please define the urgency of this ticket.");
          return;
        }
        break;
      default:
        break;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    // Skip location step when going back if location is pre-selected
    if (activeStep === 2 && selectedLocation && preSelectedLocation) {
      setActiveStep(0); // Go back to Support Type, skipping location
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleCancelClick = () => {
    // Check if user has entered any data
    const hasData = formData.content || formData.stepsTried || supportType || selectedLocation || urgency || (formData.attachmentURL && formData.attachmentURL.length > 0);

    if (hasData) {
      // Show confirmation dialog
      setCancelConfirmOpen(true);
    } else {
      // No data entered, close immediately
      if (onClose) {
        onClose();
      }
    }
  };

  const handleCancelConfirm = () => {
    setCancelConfirmOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleRemoveFile = useCallback((fileToRemove) => {
    const currentFiles = getValues('attachmentURL') || [];
    const newFiles = currentFiles.filter((file) => file !== fileToRemove);
    setValue('attachmentURL', newFiles);
  }, [getValues, setValue]);

  // Submit function
  const onSubmit = async () => {
    // Step 6 is always the submission step now (no device issue step anymore)
    const finalSubmitStep = 6;
    if (activeStep !== finalSubmitStep) {
      console.warn("Form submitted at the wrong step:", activeStep, "Expected:", finalSubmitStep);
      return;
    }

    if (loading) return;
    setLoading(true);

    // Validate location ID
    const locationId = selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID;
    if (!locationId) {
      enqueueSnackbar('Please select a valid location', { variant: 'error' });
      setLoading(false);
      return;
    }

    try {
      // Create the ticket
      const ticketData = {
        support_type: supportType,
        locationId: locationId, // Use validated location ID
        location_name: selectedLocation.friendlyName || selectedLocation.name,
        description: formData.content,
        steps_tried: formData.stepsTried,
        urgency: urgency,
        status: 'Open',
        next_step: 'Waiting for support team to assign device if needed',
        // NOTE: created_by_user_id and created_by_company_id are set by the backend from the authenticated user
        // Include device info if ticket was created from device page
        deviceId: formData.relatedDevice?.id || null
      };

      if (solutionsTried.length > 0) {
        ticketData.solutions_tried = JSON.stringify(solutionsTried);
      }

      const ticketResponse = await createTicket(ticketData);

      // Handle different response structures
      let createdTicket = ticketResponse.data;
      if (ticketResponse.data.data) {
        createdTicket = ticketResponse.data.data;
      }
      if (createdTicket.ticket) {
        createdTicket = createdTicket.ticket;
      }

      // Send email notification for new ticket
      try {

        // Email notifications are now handled by api.caro.care
      } catch (emailError) {console.error('Failed to send email notification:', emailError);
      }

      // Link uploaded attachments to the ticket
      //console.log('📋 Checking for attachments to link...');
      //console.log('📋 formData:', formData);
      //console.log('📋 formData.attachmentURL:', formData.attachmentURL);

      const attachments = formData.attachmentURL || [];
      //console.log('📋 Attachments array:', attachments);
      //console.log('📋 Attachments count:', attachments.length);

      if (attachments.length > 0) {
        setUploadingAttachments(true);

        //console.log('✅ Linking attachments to new ticket:', attachments);

        // Initialize progress tracking
        setAttachmentUploadProgress(attachments.map((file, index) => ({
          index,
          fileName: file.name,
          fileSize: file.size || 0,
          progress: 0,
          status: 'uploading'
        })));

        // Link each attachment to the ticket
        for (let i = 0; i < attachments.length; i++) {
          const file = attachments[i];

          try {
            updateProgressSafely(i, { progress: 30, status: 'uploading' });

            // Verify file has required fields
            if (!file.file_id || !file.name) {
              throw new Error(`Invalid file data: missing file_id or name. File: ${JSON.stringify(file)}`);
            }

            // Link the attachment to the ticket
            const attachmentData = {
              file_id: file.file_id,
              originalName: file.name,
              size: file.size,
              mimetype: file.mime_type,
              url: file.url
            };

            //console.log('Linking attachment to ticket:', {
            //  ticketId: createdTicket.id,
            //  data: attachmentData
            //});

            updateProgressSafely(i, { progress: 60, status: 'uploading' });
            const response = await ticketsApi.addAttachment(createdTicket.id, attachmentData);
            //console.log('Attachment linked successfully:', response.data);

            // Mark as completed
            updateProgressSafely(i, { progress: 100, status: 'completed' });
          } catch (error) {
            console.error('❌ Error linking attachment to ticket:', error);
            console.error('Error details:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
              file: file
            });
            updateProgressSafely(i, { progress: 100, status: 'error', error: error.message });

            // Show error to user
            enqueueSnackbar(
              `Failed to attach ${file.name}: ${error.response?.data?.error || error.message}`,
              { variant: 'error' }
            );
          }
        }

        setUploadingAttachments(false);
      } else {
        //console.log('⚠️ No attachments to link - skipping attachment linking');
      }

      // Show success message
      enqueueSnackbar(`Ticket #${createdTicket.id} created successfully!`, {
        variant: 'success',
        autoHideDuration: 5000
      });

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(createdTicket);
      }

      // Reset form or close if embedded
      if (embedded && onClose) {
        onClose();
      } else {
        // Reset form and go back to first step
        setActiveStep(0);
        setFormData({
          content: '',
          stepsTried: '',
          attachmentURL: [],
          relatedDevice: preSelectedDevice,
          deviceId: preSelectedDevice?.id || null
        });
        setSupportType('');
        setSelectedLocation(preSelectedLocation);
        setUrgency('');
        setSuggestedSolutions([]);
        setSolutionsChecked(false);
        setSolutionsTried([]);
      }

    } catch (error) {
      console.error('Error creating ticket:', error);
      enqueueSnackbar('Failed to create ticket. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Camera functions
  const startCamera = useCallback(async (mode) => {
    if (!hasCameraSupport) {
      alert('Camera not supported on this device');
      return;
    }

    setCameraMode(mode);
    setCameraOpen(true);

    try {
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: mode === 'video'
      };

      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraOpen(false);

      alert('Camera access denied or not available.');
    }
  }, [hasCameraSupport]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCameraOpen(false);
    setRecording(false);
    setRecordingTime(0);
  }, []);

  const takePhoto = useCallback(() => {
    if (!videoRef.current) return;

    // In a real implementation, this would capture from the video stream
    alert('Photo captured!');
    stopCamera();
  }, [stopCamera]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    // In a real implementation, this would start recording
    setRecording(true);
    setRecordingTime(0);

    intervalRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopRecording = useCallback(() => {
    if (recording) {
      // In a real implementation, this would stop recording
      setRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      alert('Video recorded!');
      stopCamera();
    }
  }, [recording, stopCamera]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Render device issue type selection
  const renderDeviceIssueTypeSelection = useCallback(() => {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>{t ? t("CreateForm.what_type_issue_are_you_having_t") : "What type of issue are you having with the device?"}

        </Typography>

        {formData.relatedDevice &&
        <Alert severity="info" sx={{ mb: 2 }} icon={<DeviceIcon />}>
            <Typography variant="body2">
              <strong>{t ? t("CreateForm.device") : "Device:"}</strong> {formData.relatedDevice.name || formData.relatedDevice.friendlyName || 'Selected Device'}
            </Typography>
            <Typography variant="caption" color="text.secondary">{t ? t("CreateForm.please_specify_what_kind_problem_your") : "Please specify what kind of problem you're experiencing with this device"}

          </Typography>
          </Alert>
        }

        <Box display="flex" justifyContent="space-around" flexWrap="wrap">
          {[
          {
            label: 'Hardware Issue',
            value: 'hardware',
            icon: <HelpIcon />,
            description: 'Physical damage, power problems, or component failure'
          },
          {
            label: 'Software Issue',
            value: 'software',
            icon: <ImportantDevicesIcon />,
            description: 'Application errors, crashes, or software bugs'
          },
          {
            label: 'Connectivity Issue',
            value: 'connectivity',
            icon: <WifiTetheringErrorIcon />,
            description: 'Network connection problems or communication issues'
          },
          {
            label: 'Configuration Issue',
            value: 'configuration',
            icon: <EditIcon />,
            description: 'Setup or settings problems'
          },
          {
            label: 'Other',
            value: 'other',
            icon: <SupportIcon />,
            description: 'Any other type of issue not listed above'
          }].
          map((option) =>
          <Button
            key={option.value}
            variant={formData.device_issue_type === option.value ? 'contained' : 'outlined'}
            onClick={() => handleChange('device_issue_type', option.value)}
            sx={{
              m: 1,
              p: 2,
              minWidth: isMobileDevice ? '100%' : 150,
              maxWidth: isMobileDevice ? 'calc(100% - 16px)' : 'none',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%'
            }}
            type="button">
            
              <Box sx={{
              width: 40,
              height: 40,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
                {option.icon}
              </Box>
              <Typography variant="subtitle2">{option.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {option.description}
              </Typography>
            </Button>
          )}
        </Box>
      </Box>);

  }, [formData.relatedDevice, formData.device_issue_type, isMobileDevice, handleChange]);

  // Render step 4 with attachments
  const renderStep4Enhanced = useCallback(() => {
    const currentFiles = formData.attachmentURL || [];
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>{t ? t("CreateForm.attach_files_optional") : "\uD83D\uDCCE Attach Files (Optional)"}

        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t ? t("CreateForm.maximum_file_size_100mb_supported_images") : "Files will be securely encrypted and stored. Maximum file size: 500MB"}

        </Typography>

        <TicketAttachmentUpload
          onFilesUploaded={(uploadedFiles) => {
            //console.log('📎 Files uploaded in CreateForm:', uploadedFiles);
            //console.log('📎 Current files before adding:', currentFiles);
            // Add uploaded files to form data
            const newFiles = [...currentFiles, ...uploadedFiles];
            //console.log('📎 New files array:', newFiles);
            handleChange('attachmentURL', newFiles);
            //console.log('📎 formData.attachmentURL after update:', formData.attachmentURL);
          }}
          compact={true}
        />

        {/* Display already uploaded files */}
        {currentFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t ? t("CreateForm.attached_files") : "Attached Files:"}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {currentFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={() => handleRemoveFile(file)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2, display: 'none' }}>
          <Field.Upload
            name="attachmentURL"
            multiple
            maxSize={20 * 1024 * 1024 * 1024}
            onDelete={handleRemoveFile}
            t={t}
            accept={{
              'image/*': [],
              'video/*': [],
              'audio/*': [],
              'application/pdf': [],
              'application/msword': [],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
              'application/vnd.ms-excel': [],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
              'text/plain': [],
              'text/csv': []
            }} />


          {isMobileDevice && hasCameraSupport &&
          <>
              <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => startCamera('photo')}
              color="secondary"
              type="button">{t ? t("CreateForm.take_photo") : "Take Photo"}


            </Button>
              <Button
              variant="outlined"
              startIcon={<VideocamIcon />}
              onClick={() => startCamera('video')}
              color="secondary"
              type="button">{t ? t("CreateForm.record_video") : "Record Video"}


            </Button>
            </>
          }
        </Box>

        {currentFiles.length > 0 &&
        <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>{t ? t("CreateForm.selected_files") : "Selected Files ("}
            {currentFiles.length}/10):
            </Typography>
            {currentFiles.map((file, index) =>
          <Box
            key={index}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1,
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}>
            
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>{getFileIcon(file)}</Typography>
                  <Box>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                </Box>
                <Button
              size="small"
              onClick={() => handleRemoveFile(file)}
              color="error"
              type="button">{t ? t("CreateForm.remove") : "Remove"}


            </Button>
              </Box>
          )}
          </Box>
        }

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t ? t("CreateForm.tip") : "Tip:"}</strong>{t ? t("CreateForm.you_can_upload_large_video_files_up_1") : "You can upload large video files up to 100MB."}
            {isMobileDevice && ' On mobile, use the camera buttons for quick photo/video capture.'}
          </Typography>
        </Alert>
      </Box>);

  }, [
  getValues,
  handleRemoveFile,
  isMobileDevice,
  hasCameraSupport,
  startCamera,
  getFileIcon,
  formatFileSize]
  );

  // Render steps
  const renderStepContent = (step) => {const { t } = useThemeSettings();
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>{t ? t("CreateForm.what_type_support_do_you_need") : "What type of support do you need?"}

            </Typography>

            {/* Show device and location info when creating from device */}
            {formData.relatedDevice &&
            <Box sx={{ mb: 2 }}>
                <Alert severity="info" icon={<DeviceIcon />}>
                  <Typography variant="body2">
                    <strong>{t ? t("CreateForm.device") : "Device:"}</strong> {formData.relatedDevice.name || formData.relatedDevice.friendlyName || 'Device Selected'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{t ? t("CreateForm.this_ticket_will_be_linked_select") : "This ticket will be linked to the selected device."}

                </Typography>
                </Alert>

                {/* Show location status for device */}
                {fetchingLocation ?
              <Alert severity="warning" sx={{ mt: 1 }} icon={<CircularProgress size={16} />}>
                    <Typography variant="body2">{t ? t("CreateForm.fetching_location_device") : "Fetching location for device..."}

                </Typography>
                  </Alert> :
              selectedLocation ?
              <Alert severity="success" sx={{ mt: 1 }} icon={<LocationIcon />}>
                    <Typography variant="body2">
                      <strong>{t ? t("DeviceStatusDashboard.location") : "Location:"}</strong> {selectedLocation.friendlyName || selectedLocation.name || `Location ${selectedLocation.id}`}
                    </Typography>
                    {selectedLocation.path &&
                <Typography variant="caption" color="text.secondary" display="block">
                        <strong>{t ? t("CreateForm.path") : "Path:"}</strong> {selectedLocation.path}
                      </Typography>
                }
                    <Typography variant="caption" color="text.secondary">{t ? t("CreateForm.both_device_location_are_automatical") : "Both device and location are automatically linked. The location step will be skipped."}

                </Typography>
                  </Alert> :

              <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">{t ? t("CreateForm.location_not_found_device_youll_need") : "Location not found for device. You'll need to select one manually."}

                </Typography>
                  </Alert>
              }
              </Box>
            }

            {/* Show location info when creating from location (without device) */}
            {!formData.relatedDevice && selectedLocation &&
            <Alert severity="success" sx={{ mb: 2 }} icon={<LocationIcon />}>
                <Typography variant="body2">
                  <strong>{t ? t("DeviceStatusDashboard.location") : "Location:"}</strong> {selectedLocation.friendlyName || selectedLocation.name || `Location ${selectedLocation.id}`}
                </Typography>
                {selectedLocation.path &&
              <Typography variant="caption" color="text.secondary" display="block">
                    <strong>{t ? t("CreateForm.path") : "Path:"}</strong> {selectedLocation.path}
                  </Typography>
              }
                <Typography variant="caption" color="text.secondary">{t ? t("CreateForm.location_has_been_automatically_set") : "The location has been automatically set. You'll skip the location selection step."}

              </Typography>
              </Alert>
            }

            {loadingSupportTypes ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Box display="flex" justifyContent="space-around" flexWrap="wrap">
                {supportTypes.map((option) =>
                <Button
                  key={option.value}
                  variant={supportType === option.value ? 'contained' : 'outlined'}
                  onClick={() => setSupportType(option.value)}
                  sx={{
                    m: 1,
                    p: 2,
                    minWidth: isMobileDevice ? '100%' : 150,
                    maxWidth: isMobileDevice ? 'calc(100% - 16px)' : 'none',
                    flexDirection: isMobileDevice ? 'row' : 'column',
                    justifyContent: isMobileDevice ? 'flex-start' : 'center',
                    gap: isMobileDevice ? 2 : 0
                  }}
                  type="button">

                    <Box sx={{
                    width: isMobileDevice ? 24 : 40,
                    height: isMobileDevice ? 24 : 40,
                    mb: isMobileDevice ? 0 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                      {getSupportTypeIcon(option.value)}
                    </Box>
                    <Typography>{option.label}</Typography>
                  </Button>
                )}
              </Box>
            )}
          </Box>);


      case 1:
        // This step might be skipped if location is pre-selected
        // If we reach here with pre-selected location, show it clearly
        if (preSelectedLocation && selectedLocation) {
          return (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">{t ? t("CreateForm.this_step_is_normally_skipped_when_locat") : "This step is normally skipped when location is pre-selected."}

                </Typography>
              </Alert>
            </Box>);

        }

        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>{t ? t("CreateForm.select_location_where_issue_is_o") : "Select the location where the issue is occurring"}

            </Typography>

            {/* Show related device info if ticket is created from device page */}
            {formData.relatedDevice &&
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t ? t("CreateForm.related_device") : "Related Device:"}</strong> {formData.relatedDevice.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">{t ? t("CreateForm.support_team_will_be_notified_about") : "The support team will be notified about this device"}

              </Typography>
              </Alert>
            }

            {selectedLocation ?
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1">
                        {selectedLocation.friendlyName || selectedLocation.name}
                      </Typography>
                      {selectedLocation.path &&
                    <Typography variant="caption" color="text.secondary">
                          {selectedLocation.path}
                        </Typography>
                    }
                    </Box>
                  </Box>
                  <Button onClick={() => setLocationTreeOpen(true)}>{t ? t("CreateForm.change") : "Change"}

                </Button>
                </Box>
              </Paper> :

            <Button
              variant="outlined"
              fullWidth
              onClick={() => setLocationTreeOpen(true)}
              startIcon={<LocationIcon />}
              sx={{ py: 2 }}>{t ? t("CreateForm.select_location") : "Select Location"}


            </Button>
            }

            <TreeSelect
              open={locationTreeOpen}
              onClose={() => setLocationTreeOpen(false)}
              onSelect={handleLocationSelect}
              data={locations}
              selectedId={selectedLocation?.id || selectedLocation?._id}
              title={t ? t("CreateForm.select_location") : "Select Location"}
              type="location"
              loading={loadingLocations}
              allowNone={false} />
            
          </Box>);


      case 2:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>{t ? t("CreateForm.describe_issue_detail") : "Describe the issue in detail"}

            </Typography>
            <Field.Editor
              name="content"
              value={formData.content}
              onChange={(value) => handleChange('content', value)}
              sx={{ maxHeight: 480 }}
              placeholder={t ? t("CreateForm.please_provide_detailed_description") : "Please provide a detailed description of the issue you're experiencing..."} />
            

            {showSolutionInfo && suggestedSolutions.length > 0 &&
            <Card sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'warning.main' }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>{t ? t("CreateForm.solutions_found") : "Solutions Found!"}</AlertTitle>{t ? t("CreateForm.weve_found_potential_solutions_your") : "We've found potential solutions for your issue. Please check if you've tried these before submitting."}

              </Alert>

                <List>
                  {suggestedSolutions.slice(0, 3).map((solution) =>
                <ListItem key={solution.id} alignItems="flex-start">
                      <ListItemIcon>
                        <Checkbox
                      onChange={(e) => handleSolutionCheck(solution.id, e.target.checked)}
                      checked={solutionsTried.includes(solution.id)} />
                    
                      </ListItemIcon>
                      <ListItemText
                    primary={solution.title}
                    secondary={
                    <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary">
                              {solution.description}
                            </Typography>
                            {solution.stepByStepInstructions &&
                      <Collapse in={true}>
                                <Typography variant="caption" component="div" sx={{ mt: 1 }}>{t ? t("CreateForm.stepbystep") : "Step-by-step:"}

                        </Typography>
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(solution.stepByStepInstructions) }} />
                              </Collapse>
                      }
                          </React.Fragment>
                    } />
                  
                    </ListItem>
                )}
                </List>

                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleSkipSolutions}
                  startIcon={<CancelOutlinedIcon />}>{t ? t("CreateForm.none_these_apply") : "None of these apply"}


                </Button>
                  <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSolutionsConfirmed}
                  startIcon={<CheckCircleOutlineIcon />}>{t ? t("CreateForm.ive_checked_these_solutions") : "I've checked these solutions"}


                </Button>
                </Box>
              </Card>
            }

            {/* Solution recommendation component */}
            {getValues("content") &&
            <SolutionRecommendation
              ticketDescription={getValues("content")}
              isCreatingTicket={true}
              onSolutionsFound={(solutions) => {
                setSuggestedSolutions(solutions);
              }} />

            }
          </Box>);


      case 3:
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>{t ? t("CreateForm.what_steps_have_you_already_tried_res") : "What steps have you already tried to resolve this issue?"}

            </Typography>
            <Field.Editor
              name="stepsTried"
              value={formData.stepsTried}
              onChange={(value) => handleChange('stepsTried', value)}
              sx={{ maxHeight: 240 }}
              placeholder={t ? t("CreateForm.list_any_troubleshooting_steps_youve_alr") : "List any troubleshooting steps you've already attempted..."} />
            
          </Box>);


      case 4:
        return renderStep4Enhanced();

      case 5:
        // Show urgency selection for step 5
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>{t ? t("CreateForm.how_urgent_is_this_issue") : "How urgent is this issue?"}

            </Typography>
            <Box display="flex" justifyContent="space-around" flexWrap="wrap">
              {[
              {
                label: 'Urgent',
                value: 'URGENT',
                icon: <WifiTetheringErrorIcon />,
                helpText: 'Immediate attention is required. Reaction will be within 2 working hours.',
                color: '#e53935'
              },
              {
                label: 'High',
                value: 'HIGH',
                icon: <PriorityHighIcon />,
                helpText: 'High priority – please address as soon as possible. Reaction will be within 1 working day.',
                color: '#fb8c00'
              },
              {
                label: 'Medium',
                value: 'MEDIUM',
                icon: <DensityMediumIcon />,
                helpText: 'Medium priority. Reaction will be within 5 working days.',
                color: '#fdd835'
              },
              {
                label: 'Low',
                value: 'LOW',
                icon: <LowPriorityIcon />,
                helpText: 'Low priority. Reaction will in combination with other raised tickets in building.',
                color: '#43a047'
              }].
              map((option) =>
              <Box key={option.value} sx={{
                position: 'relative',
                m: 1,
                width: isMobileDevice ? '100%' : 'auto',
                maxWidth: isMobileDevice ? 'calc(100% - 16px)' : 'none'
              }}>
                  <Tooltip title={option.helpText} placement="top">
                    <Button
                    variant={urgency === option.value ? 'contained' : 'outlined'}
                    onClick={() => setUrgency(option.value)}
                    sx={{
                      p: 2,
                      width: '100%',
                      minWidth: isMobileDevice ? '100%' : 150,
                      flexDirection: isMobileDevice ? 'row' : 'column',
                      justifyContent: isMobileDevice ? 'flex-start' : 'center',
                      gap: isMobileDevice ? 2 : 0,
                      borderColor: option.color,
                      position: 'relative',
                      ...(urgency === option.value ?
                      {
                        bgcolor: option.color,
                        color: 'white'
                      } :
                      {
                        color: option.color
                      })
                    }}
                    type="button">
                    
                      <Box sx={{
                      width: isMobileDevice ? 24 : 40,
                      height: isMobileDevice ? 24 : 40,
                      mb: isMobileDevice ? 0 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                        {option.icon}
                      </Box>
                      <Typography>{option.label}</Typography>
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {urgency === 'URGENT' &&
            <Alert
              severity="error"
              sx={{
                mt: 3,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': {
                    opacity: 1
                  },
                  '50%': {
                    opacity: 0.8
                  },
                  '100%': {
                    opacity: 1
                  }
                }
              }}>
              
                <AlertTitle sx={{ fontWeight: 'bold' }}>{t ? t("CreateForm.urgent_support_needed") : "\u26A0\uFE0F URGENT SUPPORT NEEDED"}</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>{t ? t("CreateForm.urgent_issues_requiring_immediate_as") : "For urgent issues requiring immediate assistance, please also call our support team directly:"}

              </Typography>
                <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1
                }}>
                
                  📞 +32 9 397 02 90
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{t ? t("CreateForm.our_support_team_is_available_during_bus") : "Our support team is available during business hours for urgent matters."}

              </Typography>
              </Alert>
            }
          </Box>);


      case 6:
        return (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
            <Typography variant="h6" mb={2}>{t ? t("CreateForm.submitting_your_ticket") : "Submitting your ticket..."}</Typography>
            <CircularProgress size={40} />
            {uploadingAttachments &&
            <Box sx={{ width: '100%', maxWidth: 500, mt: 3 }}>
                <Typography variant="subtitle1" mb={2}>{t ? t("CreateForm.uploading_attachments") : "Uploading attachments..."}</Typography>
                {attachmentUploadProgress.map((item, idx) =>
              <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2">{item.fileName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress variant="determinate" value={item.progress} />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">{`${item.progress}%`}</Typography>
                      </Box>
                    </Box>
                  </Box>
              )}
              </Box>
            }
          </Box>);


      default:
        return <div>{t ? t("CreateForm.unknown_step") : "Unknown step"}</div>;
    }
  };

  // Render component
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: embedded ? 0 : 3 }}>
      {!embedded &&
      <Box sx={{ mb: 4, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>{t ? t("CreateForm.create_new_support_ticket") : "Create New Support Ticket"}
</Typography>
          {onClose &&
          <IconButton
            onClick={handleCancelClick}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)'
            }}
            aria-label="close">

              <CloseIcon />
            </IconButton>
          }
        </Box>
      }

      <form onSubmit={(e) => e.preventDefault()}>
        <Card sx={{ p: 3 }}>
          <Stepper activeStep={selectedLocation && activeStep > 0 ? activeStep - 1 : activeStep} alternativeLabel>
            {[
            'Support Type',
            ...(selectedLocation ? [] : ['Location']), // Hide location step if location is already selected
            'Describe Issue',
            'Troubleshooting',
            'Attachments',
            'Urgency'].
            map((label, index) =>
            <Step key={label} completed={index < (selectedLocation && activeStep > 0 ? activeStep - 1 : activeStep)}>
                <StepLabel>{label}</StepLabel>
              </Step>
            )}
          </Stepper>

          <Box sx={{ mt: 4, mb: 2 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation buttons */}
          {activeStep < 6 &&
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Box>
                {activeStep > 0 &&
              <Button variant="outlined" onClick={handleBack} type="button">{t ? t("CreateForm.back") : "Back"}

              </Button>
              }
                {onClose &&
              <Button onClick={handleCancelClick} sx={{ ml: 1 }}>{t ? t("LocationDialog.cancel") : "Cancel"}

              </Button>
              }
              </Box>
              {activeStep === 5 ?
            <Button variant="contained" type="submit" onClick={finalStep} disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Submit Ticket'}
                </Button> :

            <Button variant="contained" onClick={handleNext} type="button">{t ? t("CreateForm.next") : "Next"}

            </Button>
            }
            </Box>
          }
        </Card>
      </form>

      {/* Camera Dialog */}
      {cameraOpen &&
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column'
        }}>
        
          <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            color: 'white'
          }}>
          
            <Typography variant="h6">
              {cameraMode === 'photo' ? '📷 Take Photo' : '🎥 Record Video'}
            </Typography>
            <Button
            onClick={stopCamera}
            color="inherit"
            sx={{ color: 'white' }}
            type="button">{t ? t("CreateForm.close") : "\u2715 Close"}


          </Button>
          </Box>

          <Box sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100vw',
              maxHeight: 'calc(100vh - 200px)',
              objectFit: 'contain'
            }} />
          

            {recording &&
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 1
            }}>
            
                <FiberManualRecordIcon />
                <Typography variant="caption">
                  {formatTime(recordingTime)}
                </Typography>
              </Box>
          }
          </Box>

          <Box sx={{ p: 2, textAlign: 'center' }}>
            {cameraMode === 'photo' ?
          <Button
            variant="contained"
            size="large"
            onClick={takePhoto}
            startIcon={<PhotoCameraIcon />}
            type="button">{t ? t("CreateForm.take_photo") : "Take Photo"}


          </Button> :

          <Box>
                {!recording ?
            <Button
              variant="contained"
              size="large"
              onClick={startRecording}
              startIcon={<VideocamIcon />}
              color="error"
              type="button">{t ? t("CreateForm.start_recording") : "Start Recording"}


            </Button> :

            <Button
              variant="contained"
              size="large"
              onClick={stopRecording}
              startIcon={<StopIcon />}
              color="primary"
              type="button">{t ? t("CreateForm.stop_recording") : "Stop Recording"}


            </Button>
            }
              </Box>
          }
          </Box>
        </Box>
      }

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}>

        <DialogTitle>{t ? t("CreateForm.cancel_ticket_creation") : "Cancel Ticket Creation?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t ? t("CreateForm.cancel_confirmation_message") : "Are you sure you want to cancel? All entered information will be lost."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>{t ? t("CreateForm.continue_editing") : "Continue Editing"}</Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained">
            {t ? t("LocationDialog.cancel") : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>);

}